import { cn } from '@/lib/utils';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

type AnimationType = 
  | 'fade-in' 
  | 'fade-in-up' 
  | 'fade-in-down' 
  | 'slide-up' 
  | 'slide-in-right' 
  | 'slide-in-left' 
  | 'scale-in' 
  | 'blur-in';

interface AnimatedSectionProps {
  children: React.ReactNode;
  animation?: AnimationType;
  delay?: number;
  className?: string;
}

export function AnimatedSection({
  children,
  animation = 'fade-in-up',
  delay = 0,
  className,
}: AnimatedSectionProps) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={cn(
        'opacity-0',
        isVisible && `animate-${animation}`,
        className
      )}
      style={{ 
        animationDelay: isVisible ? `${delay}ms` : '0ms',
        animationFillMode: 'forwards'
      }}
    >
      {children}
    </div>
  );
}

interface StaggeredContainerProps {
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
  animation?: AnimationType;
}

export function StaggeredContainer({
  children,
  className,
  staggerDelay = 100,
  animation = 'fade-in-up',
}: StaggeredContainerProps) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <div ref={ref} className={className}>
      {Array.isArray(children)
        ? children.map((child, index) => (
            <div
              key={index}
              className={cn('opacity-0', isVisible && `animate-${animation}`)}
              style={{
                animationDelay: isVisible ? `${index * staggerDelay}ms` : '0ms',
                animationFillMode: 'forwards',
              }}
            >
              {child}
            </div>
          ))
        : children}
    </div>
  );
}
